import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import { mapLevel } from '../utils/languageUtils';
import JapaneseExamResult from './JapaneseExamResult';
import JapaneseSectionIntro from './JapaneseSectionIntro';
import {
  VocabQuestion,
  GrammarReadingQuestion,
  ListeningQuestion,
} from './JapaneseExamRenderers';
import './BrainWarmup.css';
import './ExamPrep.css';
import './JapaneseExamPrep.css';
import './JapaneseExamQuestions.css';

// Flow phases: 'intro' (per-section gate) -> 'questions' -> next 'intro' -> ... -> 'result'
const PHASE = { INTRO: 'intro', QUESTIONS: 'questions', RESULT: 'result' };

// Fixed section order (valid even before the exam questions are loaded).
const SECTION_ORDER = ['vocab', 'grammar_reading', 'listening'];

// Max break between sections (minutes) — the next section auto-starts at 0.
const BREAK_MINUTES = 10;

// Per-section durations (minutes) by level + section key (matches jlpt_exam_config seed).
const SECTION_DURATIONS = {
  N5: { vocab: 25, grammar_reading: 55, listening: 30 },
  N4: { vocab: 30, grammar_reading: 55, listening: 35 },
  N3: { vocab: 30, grammar_reading: 70, listening: 40 },
};

// Display metadata per section key (for stepper + intro + headings).
const SECTION_META = {
  vocab: {
    letter: 'A',
    shortName: 'Character & Vocabulary',
    startName: 'Characters and Vocabulary',
    fullName: 'Language Knowledge (Characters & Vocabulary)',
    jaHeading: 'Aセクション：言語知識（文字・語彙）',
    description:
      'Tests your knowledge of Japanese written characters and vocabulary. All questions are MCQs with four options.',
  },
  grammar_reading: {
    letter: 'B',
    shortName: 'Grammar and Reading',
    startName: 'Grammar and Reading',
    fullName: 'Language Knowledge (Grammar & Reading)',
    jaHeading: 'Bセクション：言語知識（文法）・読解',
    description:
      'Tests your knowledge of Japanese grammar and your reading comprehension. All questions are MCQs with four options.',
  },
  listening: {
    letter: 'C',
    shortName: 'Listening',
    startName: 'Listening',
    fullName: 'Listening',
    jaHeading: 'Cセクション：聴解',
    description:
      'Tests your listening comprehension. Play each audio clip and choose the best answer. Audio can be played a limited number of times.',
  },
};

function formatClock(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

const JapaneseExamQuestions = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  usePageTimeTracker('exam-prep', user);

  const navState = location.state || {};
  const { proficiencyLevel = 'beginner-n5' } = navState;
  const levelCode = mapLevel(proficiencyLevel); // 'N5' | 'N4' | 'N3'
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';

  // The exam is GENERATED on the landing page's "Start Exam Prep" and passed here
  // via nav state (no attempt yet). The attempt is CREATED when the user clicks
  // Start Section A (startSection calls /start with genToken). No agent call here.
  const [exam] = useState(() => navState.exam || null);
  const genToken = navState.genToken || null;
  // Attempt is created on "Start Exam Prep" (landing page) and passed in here, so
  // Start Section A just reveals the questions (no /start call needed).
  const [attemptId, setAttemptId] = useState(() => navState.attemptId || null);
  const [loadError, setLoadError] = useState('');
  const [starting, setStarting] = useState(false);

  const [phase, setPhase] = useState(PHASE.INTRO);
  const [activeSection, setActiveSection] = useState(0);
  const [currentItem, setCurrentItem] = useState(0);
  const [answers, setAnswers] = useState({});   // { qid: optionNum }
  const [skippedSet, setSkippedSet] = useState({}); // { qid: true } explicitly skipped
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false); // overlay from submit until result loads
  const [sectionTimes, setSectionTimes] = useState({}); // { sectionKey: seconds }
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [showSubmitPopup, setShowSubmitPopup] = useState(false);
  const [breakLeft, setBreakLeft] = useState(0);       // seconds left in the between-section break
  const breakDeadlineRef = useRef(null);
  const breakTimerRef = useRef(null);
  // Listening: how many plays have been consumed per question. Persisted here (not
  // inside the audio player) so the single-play limit survives navigating away and
  // back to the same question — once a question's audio is played, it stays locked.
  const [audioPlaysUsed, setAudioPlaysUsed] = useState({}); // { qid: count }
  const consumePlay = useCallback((qid) => {
    setAudioPlaysUsed((prev) => ({ ...prev, [qid]: (prev[qid] || 0) + 1 }));
  }, []);

  // Intro is driven by config (NO attempt created). The attempt + questions are
  // only created when the user clicks "Start Section A". Refreshing the intro is
  // therefore safe and never burns an attempt.
  const [cfg, setCfg] = useState(null);

  const sections = useMemo(() => (exam ? exam.sections : []), [exam]);

  // Detect a real page reload of THIS route (vs. a normal in-app navigation into
  // it). The browser's History API preserves location.state across a hard refresh,
  // so `exam` can still be present after reload. We set an in-memory sentinel on
  // the first mount of a live session; it's wiped on a full document reload, so if
  // the exam is present but the sentinel is gone, this mount is a refresh.
  const isReload = (() => {
    try {
      return !!(exam && !window.__jeqSessionAlive);
    } catch (e) { return false; }
  })();

  // If the exam wasn't passed in (direct navigation) OR the page was reloaded,
  // send the user back to the landing page to Start Exam Prep (regenerates exam).
  useEffect(() => {
    if (!exam || isReload) {
      navigate('/japanese-exam-prep', { replace: true, state: { language: 'ja', proficiencyLevel } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the level config for the intro screen (section chip values).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/japanese-exam/config?level=${levelCode}&_t=${Date.now()}`, { withCredentials: true });
        if (cancelled) return;
        if (res.data && res.data.success) setCfg(res.data.config);
      } catch (err) {
        // Non-fatal: intro can still render with sensible defaults.
        if (!cancelled) console.error('Config load failed:', err.response?.data || err.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelCode]);

  // Section config helper for the intro (questions/marks/pass/duration per section).
  const sectionConfig = (idx) => {
    const codes = ['A', 'B', 'C'];
    const s = cfg && cfg.sections ? cfg.sections.find((x) => x.sectionCode === codes[idx]) : null;
    return s || null;
  };

  const durations = SECTION_DURATIONS[levelCode] || SECTION_DURATIONS.N5;
  // Section identity comes from the fixed order (valid even before the exam loads).
  const sectionKey = SECTION_ORDER[activeSection] || 'vocab';
  const meta = SECTION_META[sectionKey] || SECTION_META.vocab;
  const section = sections.find((s) => s.key === sectionKey) || null; // questions (after Start)
  const sectionDurationSecs = (durations[sectionKey] || 30) * 60;

  const [timeLeft, setTimeLeft] = useState(sectionDurationSecs);
  const deadlineRef = useRef(null);
  const timerRef = useRef(null);
  const scrollRef = useRef(null);
  const sectionStartRef = useRef(null);

  // Group this section's questions into PAGES by pageId (text_cloze blanks and a
  // reading passage's questions share one page; everything else is its own page).
  const pages = useMemo(() => {
    const qs = section ? section.questions : [];
    const byPage = [];
    const index = {};
    qs.forEach((q) => {
      const pid = q.pageId || q.qid;
      if (index[pid] == null) { index[pid] = byPage.length; byPage.push([]); }
      byPage[index[pid]].push(q);
    });
    return byPage;
  }, [section]);

  const items = section ? section.questions : [];      // flat (for qnav + stats)
  const currentPage = pages[currentItem] || [];          // questions on the current page
  const isLastSection = activeSection === SECTION_ORDER.length - 1;
  const isLastItem = currentItem === pages.length - 1;   // currentItem is now a PAGE index
  // Save and Next is enabled once AT LEAST ONE question on the page is answered.
  // Any unanswered questions on the page are treated as "not attempted" (skipped).
  const pageAnswered = currentPage.some((q) => answers[q.qid] != null);

  // finishExam accepts the final section's elapsed seconds so the total time is
  // correct even though setSectionTimes (state) updates asynchronously.
  const finishExam = useCallback(async (finalSectionSecs = 0, finalSectionKey = null) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setSubmitting(true); // show the "Almost there!" overlay until the result loads
    const base = Object.values(sectionTimes).reduce((a, b) => a + (b || 0), 0);
    // Avoid double-counting if the final section's time was already recorded.
    const extra = finalSectionKey && sectionTimes[finalSectionKey] == null ? (finalSectionSecs || 0) : 0;
    const totalTime = base + extra;
    try {
      const res = await axios.post(
        `${API_BASE}/japanese-exam/submit`,
        { attempt_id: attemptId, level: levelCode, time_taken: totalTime },
        { withCredentials: true }
      );
      if (res.data && res.data.success) setResults(res.data.result);
    } catch (err) {
      // Even if submit fails, show the result page; it will fetch the attempt.
      console.error('Submit failed:', err.response?.data || err.message);
    }
    setPhase(PHASE.RESULT);
  }, [API_BASE, attemptId, levelCode, sectionTimes]);

  // Submit the current section: record time taken, then go to next section's intro or finish.
  const submitSection = useCallback(() => {
    const elapsed = sectionStartRef.current ? Math.round((Date.now() - sectionStartRef.current) / 1000) : 0;
    setSectionTimes((prev) => ({ ...prev, [sectionKey]: elapsed }));
    if (timerRef.current) clearInterval(timerRef.current);
    if (isLastSection) {
      // Auto-submit path (timer expiry on the last section) — score immediately.
      finishExam(elapsed, sectionKey);
      return;
    }
    setActiveSection((s) => s + 1);
    setCurrentItem(0);
    setPhase(PHASE.INTRO);
  }, [sectionKey, isLastSection, finishExam]);

  // Stats for a single section (the "Ready to Submit?" popup shows the CURRENT
  // section's counts — Total / Answered / Skipped / Attempted%).
  const computeSectionStats = useCallback(() => {
    const sec = sections.find((s) => s.key === sectionKey);
    let total = 0, answered = 0, skipped = 0;
    (sec ? sec.questions : []).forEach((q) => {
      total += 1;
      if (answers[q.qid] != null) answered += 1;
      else if (skippedSet[q.qid]) skipped += 1;
    });
    const attemptedPct = total > 0 ? Math.round((answered / total) * 100) : 0;
    return { total, answered, skipped, attemptedPct };
  }, [sections, sectionKey, answers, skippedSet]);

  // Open the "Ready to Submit?" popup on the last item of ANY section. The timer
  // keeps running (its deadline is fixed) so "Go Back and re-check" simply resumes;
  // time is only recorded on confirm.
  const openSubmitPopup = useCallback(() => {
    setShowSubmitPopup(true);
  }, []);

  // Confirm from the popup: record this section's time, then either advance to the
  // next section's intro (non-final) or finish the exam (final section).
  const confirmSubmitSection = useCallback(() => {
    setShowSubmitPopup(false);
    const elapsed = sectionStartRef.current ? Math.round((Date.now() - sectionStartRef.current) / 1000) : 0;
    setSectionTimes((prev) => ({ ...prev, [sectionKey]: elapsed }));
    if (timerRef.current) clearInterval(timerRef.current);
    if (isLastSection) {
      finishExam(elapsed, sectionKey);
      return;
    }
    // Advance to the next section's intro and start the 10-minute break countdown.
    setActiveSection((s) => s + 1);
    setCurrentItem(0);
    setPhase(PHASE.INTRO);
    breakDeadlineRef.current = Date.now() + BREAK_MINUTES * 60 * 1000;
    setBreakLeft(BREAK_MINUTES * 60);
  }, [sectionKey, isLastSection, finishExam]);

  // Start the active section from its intro screen.
  //  - First section (no attempt yet): CREATE the attempt now (/start). This is the
  //    point that consumes 1 of the 2/24h attempts and starts the clock.
  //  - Later sections: just reveal the already-loaded questions.
  const startSection = useCallback(async () => {
    if (!attemptId) {
      try {
        setStarting(true);
        setLoadError('');
        const res = await axios.post(
          `${API_BASE}/japanese-exam/start`,
          { level: levelCode, gen_token: genToken },
          { withCredentials: true }
        );
        if (res.data && res.data.success) {
          setAttemptId(res.data.attempt_id);
        } else {
          setLoadError(res.data?.error || 'Failed to start the section.');
          setStarting(false);
          return;
        }
      } catch (err) {
        setLoadError(err.response?.data?.error || 'Failed to start the section. Please try again.');
        setStarting(false);
        return;
      }
      setStarting(false);
    }
    // Stop any running break countdown now that the section is starting.
    if (breakTimerRef.current) { clearInterval(breakTimerRef.current); breakTimerRef.current = null; }
    breakDeadlineRef.current = null;
    setPhase(PHASE.QUESTIONS);
    setCurrentItem(0);
    sectionStartRef.current = Date.now();
  }, [API_BASE, attemptId, genToken, levelCode]);

  // Section countdown (runs only while answering).
  useEffect(() => {
    if (phase !== PHASE.QUESTIONS) return;
    deadlineRef.current = Date.now() + sectionDurationSecs * 1000;
    setTimeLeft(sectionDurationSecs);
    const tick = () => {
      const remaining = Math.max(0, Math.round((deadlineRef.current - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        submitSection();
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, activeSection]);

  // Scroll to top when question/section/phase changes.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    try { window.scrollTo(0, 0); } catch (e) { /* noop */ }
  }, [activeSection, currentItem, phase]);

  // Between-section break countdown (only on an intro that has an active break
  // deadline — i.e. after submitting a previous section). Auto-starts the next
  // section when the break runs out. Cleared once the section starts.
  useEffect(() => {
    if (phase !== PHASE.INTRO || !breakDeadlineRef.current) return;
    const tick = () => {
      const remaining = Math.max(0, Math.round((breakDeadlineRef.current - Date.now()) / 1000));
      setBreakLeft(remaining);
      if (remaining <= 0) {
        if (breakTimerRef.current) clearInterval(breakTimerRef.current);
        breakDeadlineRef.current = null;
        startSection(); // auto-start the next section when the break ends
      }
    };
    tick();
    breakTimerRef.current = setInterval(tick, 1000);
    return () => { if (breakTimerRef.current) clearInterval(breakTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, activeSection]);

  // Once the exam has started (attempt created), warn on refresh/close/navigate-away.
  // The browser shows its native confirm dialog; staying keeps the user on the page,
  // leaving ends the attempt (progress not saved) — same behaviour as other exam pages.
  // Only on the QUESTION pages: section landing pages refresh normally (no prompt).
  const examStarted = attemptId != null && phase === PHASE.QUESTIONS;
  useEffect(() => {
    if (!examStarted) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [examStarted]);

  // Browser Back on a question page: stay put and show the Exit confirmation popup.
  // Confirm -> /japanese-exam-prep (confirmExit); Cancel -> continue the section.
  // A duplicate history entry is pushed once so Back lands on it instead of leaving.
  const backGuardPushedRef = useRef(false);
  useEffect(() => {
    if (!examStarted) return;
    if (!backGuardPushedRef.current) {
      window.history.pushState(window.history.state, '', window.location.href);
      backGuardPushedRef.current = true;
    }
    const onPop = () => {
      window.history.pushState(window.history.state, '', window.location.href);
      setShowExitPopup(true);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [examStarted]);

  // Persist a single answer to the backend (fire-and-forget; non-blocking).
  const persistAnswer = useCallback((qid, userAnswer) => {
    if (!attemptId || !qid) return;
    axios.post(
      `${API_BASE}/japanese-exam/answer`,
      { attempt_id: attemptId, level: levelCode, qid, user_answer: userAnswer },
      { withCredentials: true }
    ).catch((err) => console.error('Save answer failed:', err.response?.data || err.message));
  }, [API_BASE, attemptId, levelCode]);

  const selectAnswer = (qid, optionNum) => {
    setAnswers((prev) => ({ ...prev, [qid]: optionNum }));
    setSkippedSet((prev) => { if (!prev[qid]) return prev; const n = { ...prev }; delete n[qid]; return n; });
    persistAnswer(qid, optionNum);
  };

  // Jump to the PAGE that contains a given flat question index (qnav click).
  const goToItem = (flatIdx) => {
    const q = items[flatIdx];
    if (!q) return;
    const pid = q.pageId || q.qid;
    const pageIdx = pages.findIndex((pg) => (pg[0].pageId || pg[0].qid) === pid);
    if (pageIdx >= 0) setCurrentItem(pageIdx);
  };
  const goPrev = () => { if (currentItem > 0) setCurrentItem((i) => i - 1); };
  const saveAndNext = () => {
    // Persist answered questions; mark any UNanswered question on the page as
    // skipped (not attempted) so partial grouped pages are recorded correctly.
    currentPage.forEach((q) => {
      if (answers[q.qid] != null) {
        persistAnswer(q.qid, answers[q.qid]);
      } else {
        setSkippedSet((prev) => ({ ...prev, [q.qid]: true }));
        persistAnswer(q.qid, '__skipped__');
      }
    });
    if (!isLastItem) { setCurrentItem((i) => i + 1); return; }
    openSubmitPopup(); // last page -> confirm popup
  };
  const skip = () => {
    // Mark every unanswered question on this page as skipped.
    currentPage.forEach((q) => {
      if (answers[q.qid] == null) {
        setSkippedSet((prev) => ({ ...prev, [q.qid]: true }));
        persistAnswer(q.qid, '__skipped__');
      }
    });
    if (!isLastItem) { setCurrentItem((i) => i + 1); return; }
    openSubmitPopup();
  };

  // Exit confirmation popup (same styled modal as the other exam pages).
  const handleExit = () => {
    // Before the attempt is created (still on the first intro, not started), exit
    // straight away — nothing to lose. Otherwise show the confirm popup.
    if (phase === PHASE.INTRO && activeSection === 0) {
      navigate('/japanese-exam-prep', { state: { language: 'ja', proficiencyLevel } });
      return;
    }
    setShowExitPopup(true);
  };
  const confirmExit = () => {
    setShowExitPopup(false);
    navigate('/japanese-exam-prep', { state: { language: 'ja', proficiencyLevel } });
  };

  // Flat position (1-based) of a question across the whole section — for "問題 N / total".
  const flatPos = (qid) => items.findIndex((q) => q.qid === qid);

  const renderQuestion = () => {
    if (currentPage.length === 0) return null;
    const common = {
      page: currentPage,
      total: items.length,
      answers,
      flatPos,
      onSelect: (qid, n) => selectAnswer(qid, n),
    };
    if (sectionKey === 'vocab') return <VocabQuestion {...common} />;
    if (sectionKey === 'grammar_reading') return <GrammarReadingQuestion {...common} />;
    return <ListeningQuestion {...common} playsUsed={audioPlaysUsed} onConsumePlay={consumePlay} />;
  };

  // Exit confirmation popup — same styled modal as the other exam pages. Rendered
  // in both the intro and question phases so Exit works consistently after Start.
  const renderExitPopup = () => {
    if (!showExitPopup) return null;
    return (
      <div className="bw-confirm-overlay">
        <div className="bw-confirm-modal" style={{ padding: '0', width: '580px', minWidth: 'auto' }} onClick={e => e.stopPropagation()}>
          <div style={{ borderTop: '3px solid #7500C0', padding: '16px 32px 0' }}>
            <h3 style={{ fontFamily: 'Graphik', fontSize: '20px', fontWeight: '400', margin: '0 0 16px' }}>Exit Exam Prep?</h3>
          </div>
          <div style={{ padding: '0 32px' }}>
            <div style={{ background: '#FAFAFA', border: '1px solid #CFCFCF', padding: '12px 16px', display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <img src="/images/warningicon.svg" alt="" width="24" height="24" onError={e => { e.target.style.display = 'none'; }} />
              <span style={{ fontFamily: 'Graphik', fontSize: '14px' }}>The progress will be lost and you will need to start over from the beginning. This cannot be undone.</span>
            </div>
            <p style={{ fontFamily: 'Graphik-Semibold', fontSize: '14px', color: '#555', margin: '0 0 12px' }}>What you will lose</p>
            <div style={{ border: '1px solid #CFCFCF' }}>
              {SECTION_ORDER.map((secKey, i) => {
                const m = SECTION_META[secKey] || {};
                const isCompleted = i < activeSection;
                const isInProgress = i === activeSection;
                const icon = isCompleted ? '/images/doneicon.svg' : isInProgress ? '/images/inprogressicon.svg' : '/images/yettostarticon.svg';
                const label = isCompleted ? 'Completed' : isInProgress ? 'Currently in progress' : 'Not Started yet';
                const bg = isInProgress ? '#E6DCFF' : 'transparent';
                const border = isInProgress ? '#7500C0' : '#CFCFCF';
                return (
                  <div key={secKey} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: i < SECTION_ORDER.length - 1 ? '1px solid #CFCFCF' : 'none' }}>
                    <span style={{ fontFamily: 'Graphik-Semibold', fontSize: '16px' }}>Section {m.letter} ({m.shortName})</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: 'Graphik', fontSize: '13px', padding: '6px 14px', borderRadius: '20px', border: '1px solid ' + border, background: bg, color: '#000' }}>
                      <img src={icon} alt="" width="16" height="16" onError={e => { e.target.style.display = 'none'; }} />{label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', background: '#F1F1EF', padding: '12px 32px', marginTop: '24px' }}>
            <button style={{ padding: '10px 24px', background: 'transparent', border: '1px solid #000', fontFamily: 'Graphik-Semibold', fontSize: '14px', cursor: 'pointer' }} onClick={confirmExit}>Yes, exit and lose progress</button>
            <button style={{ padding: '10px 24px', background: '#000', color: '#fff', border: '1px solid #000', fontFamily: 'Graphik-Semibold', fontSize: '14px', cursor: 'pointer' }} onClick={() => setShowExitPopup(false)}>Continue Exam Prep</button>
          </div>
        </div>
      </div>
    );
  };

  // No exam in state (direct nav/refresh) — the redirect effect is sending the user
  // back to the landing page; render nothing in the meantime.
  if (!exam) return null;

  // ---- Result phase ----
  // On reload / direct navigation, don't render the Section intro — render nothing
  // while the redirect effect sends the user back to the landing page so they can
  // start the exam fresh (no stray agent call). `isReload` covers the case where
  // the browser preserved location.state (and thus `exam`) across a hard refresh.
  if ((!exam || isReload) && phase !== PHASE.RESULT) {
    return null;
  }

  if (phase === PHASE.RESULT) {
    return (
      <>
        <JapaneseExamResult
          user={user}
          levelCode={levelCode}
          attemptId={attemptId}
          result={results}
          sectionMeta={SECTION_META}
          sectionTimes={sectionTimes}
          onReady={() => setSubmitting(false)}
          onExit={() => navigate('/exam-prep-list')}
        />
        {/* "Almost there!" overlay stays up from submit until the result page has
            fetched its data (onReady), matching the Exam Prep submit experience. */}
        {submitting && (
          <div className="bw-confirm-overlay">
            <div className="ep-gen-loading">
              <img src="/images/loadingicon.svg" alt="" className="ep-submitting-icon" />
              <h3 className="ep-gen-loading-title">Almost there!</h3>
              <p className="ep-gen-loading-text">We&#8217;re checking your answers and putting together your results. This may take a moment.</p>
            </div>
          </div>
        )}
      </>
    );
  }

  // ---- Section intro phase ----
  // Rendered from config (before the attempt is created). The attempt + questions
  // are only created when the user clicks Start inside this screen.
  if (phase === PHASE.INTRO) {
    const sc = sectionConfig(activeSection);
    const introSection = {
      key: sectionKey,
      // Prefer loaded questions (after Start); else fall back to config/defaults.
      totalQuestions: section ? section.questions.length : (sc ? sc.numQuestions : 0),
      totalMarks: section ? section.totalMarks : (sc ? sc.totalScore : 60),
      passMarks: section ? section.passMarks : (sc ? sc.passingScore : 19),
    };
    // When arriving after submitting a previous section, show the "Completed"
    // banner for that section + the break countdown (SS2).
    const completedMeta = activeSection > 0 ? SECTION_META[SECTION_ORDER[activeSection - 1]] : null;
    const breakActive = activeSection > 0 && !!breakDeadlineRef.current;
    return (
      <>
        <JapaneseSectionIntro
          user={user}
          levelCode={levelCode}
          section={introSection}
          meta={meta}
          durationMin={(sc && sc.durationMinutes) || durations[sectionKey] || 30}
          starting={starting}
          loadError={loadError}
          completedMeta={completedMeta}
          breakActive={breakActive}
          breakLeft={breakLeft}
          breakTotalSecs={BREAK_MINUTES * 60}
          onStart={startSection}
          onExit={handleExit}
          attemptsLeft={navState.attemptsLeft}
        />
        {renderExitPopup()}
      </>
    );
  }

  // ---- Questions phase ----
  // Bar represents REMAINING time (full at start, drains toward 0) — matches Figma.
  const pct = sectionDurationSecs ? (timeLeft / sectionDurationSecs) * 100 : 0;
  const answeredCurrent = pageAnswered;

  return (
    <div className="exam-prep-page quiz-active jep-page jeq-page">
      <Header user={user} />
      <SideNav />

      <main className="jeq-main">
        {/* Title row — same structure/styling as the standard exam page */}
        <div className="bw-top-bar jeq-top-bar">
          <div className="bw-title-row">
            <span className="bw-title-purple">Exam Prep</span>
            <span className="bw-title-dash">-</span>
            <span className="bw-title-lang">Japanese</span>
            <img className="jeq-flag" src="/images/flag-japanese.svg" alt="" width="24" height="18" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
          <button className="bw-exit-btn" onClick={handleExit}>Exit Exam Prep</button>
        </div>

        {/* Section stepper + level */}
        <div className="jeq-stepper-row">
          <div className="jeq-stepper">
            {sections.map((sec, i) => {
              const sm = SECTION_META[sec.key] || {};
              const state = i < activeSection ? 'done' : i === activeSection ? 'current' : 'upcoming';
              return (
                <React.Fragment key={sec.key}>
                  {i > 0 && <span className="jeq-step-line" />}
                  <span className={`jeq-step jeq-step-${state}`}>
                    <span className="jeq-step-dot" />
                    <span className="jeq-step-label">{sm.letter}. {sm.shortName}</span>
                  </span>
                </React.Fragment>
              );
            })}
          </div>
          <span className="jeq-level-text">Level {levelCode}</span>
        </div>

        {/* Lock warning */}
        <div className="jeq-warning-line">
          <span className="jeq-warning-tri">⚠</span>
          Section {meta.letter} will be <strong>locked after submitting.</strong> You cannot return to or change your answers.
        </div>

        {/* Time bar — shows REMAINING time */}
        <div className="jeq-time-block">
          <div className="jeq-time-label">Time remaining <strong>{formatClock(timeLeft)}</strong></div>
          <div className="jeq-time-bar">
            <div className="jeq-time-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="jeq-warning-line jeq-warning-sub">
          <span className="jeq-warning-tri">⚠</span>
          <strong>Auto-submit</strong> - Once {durations[sectionKey]} minutes are up, this section submits automatically. You cannot return afterward.
        </div>

        {/* Question card (contains question + the qnav/actions footer, per Figma) */}
        <div className="jeq-card">
          <div className="jeq-card-scroll" ref={scrollRef}>
            <h2 className="jeq-card-ja-heading">{meta.jaHeading}</h2>
            <div className="jeq-card-divider" />
            {renderQuestion()}
          </div>

          {/* In-card footer: qnav grid + actions (pinned at the card bottom) */}
          <div className="jeq-card-footer-divider" />
          <div className="jeq-footer">
            <div className="jeq-qnav">
              <span className="jeq-qnav-label">
                {(() => {
                  const idxs = currentPage.map((q) => items.findIndex((x) => x.qid === q.qid)).filter((n) => n >= 0);
                  if (idxs.length === 0) return `Question 1/ ${items.length}`;
                  const lo = Math.min(...idxs) + 1, hi = Math.max(...idxs) + 1;
                  return lo === hi ? `Question ${lo}/ ${items.length}` : `Question ${lo}-${hi}/ ${items.length}`;
                })()}
              </span>
              <div className="jeq-qnav-grid">
                {items.map((q, i) => {
                  const answered = answers[q.qid] != null;
                  const isSkipped = skippedSet[q.qid] && !answered;
                  const isCurrent = currentPage.some((pq) => pq.qid === q.qid);
                  let cls = 'jeq-qnav-cell';
                  if (isCurrent) cls += ' current';
                  else if (answered) cls += ' done';
                  else if (isSkipped) cls += ' skipped';
                  return (
                    <button key={q.qid} className={cls} onClick={() => goToItem(i)}>{i + 1}</button>
                  );
                })}
              </div>
            </div>
            <div className="jeq-footer-actions">
              <button className="jeq-btn-skip" onClick={skip}>Skip</button>
              <button className="jeq-btn-prev" onClick={goPrev} disabled={currentItem === 0}>Previous</button>
              {isLastItem ? (
                // Last question of the page: Sections A/B show "Next Section",
                // the final section (C) shows "Submit Exam Prep". Both open the
                // Ready-to-Submit popup (which confirms before advancing/finishing).
                <button className="jeq-btn-next" onClick={openSubmitPopup} disabled={!answeredCurrent}>
                  {isLastSection ? 'Submit Exam Prep' : 'Next Section'}
                </button>
              ) : (
                <button className="jeq-btn-next" onClick={saveAndNext} disabled={!answeredCurrent}>Save and Next</button>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {renderExitPopup()}

      {/* "Ready to Submit?" popup — scoped to the current section (same as the
          other exam page). Final section submits the whole exam. */}
      {showSubmitPopup && (() => {
        const stats = computeSectionStats();
        const sectionName = `Section ${meta.letter} : ${meta.shortName}`;
        return (
          <div className="bw-confirm-overlay">
            <div className="bw-confirm-modal ep-submit-modal" onClick={e => e.stopPropagation()}>
              <div className="ep-submit-head">Ready to Submit?</div>
              <div className="ep-submit-body">
                <p className="ep-submit-lead">
                  {isLastSection
                    ? `You're about to submit ${sectionName} and finish your Exam Prep.`
                    : `You're about to submit ${sectionName}.`}
                </p>
                <div className="ep-submit-stats">
                  <span className="ep-submit-stat total"><strong>{stats.total}</strong> Total</span>
                  <span className="ep-submit-stat answer"><strong>{stats.answered}</strong> Answer</span>
                  <span className="ep-submit-stat skipped"><strong>{stats.skipped}</strong> Skipped</span>
                  <span className="ep-submit-stat attempt"><strong>{stats.attemptedPct}%</strong> Attempted</span>
                </div>
                {stats.skipped > 0 && (
                  <p className="ep-submit-warn"><img src="/images/yellowwarning.svg" alt="" width="14" height="14" onError={e => { e.target.style.display = 'none'; }} /> <strong>{stats.skipped} Skipped.</strong> Recheck all questions once before submitting your answer.</p>
                )}
                <p className="ep-submit-note">Any skipped questions will be submitted as not attempted, and you won't be able to make changes afterward.</p>
              </div>
              <div className="ep-submit-actions">
                <button className="ep-submit-back" onClick={() => setShowSubmitPopup(false)}>Go Back and re-check</button>
                <button className="ep-submit-confirm" onClick={confirmSubmitSection}>
                  {isLastSection ? 'Submit Exam Prep' : `Submit Section ${meta.letter}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* "Almost there!" overlay shown IMMEDIATELY after the final Submit Exam Prep
          (while /submit runs); the result phase keeps it up until data loads. */}
      {submitting && (
        <div className="bw-confirm-overlay">
          <div className="ep-gen-loading">
            <img src="/images/loadingicon.svg" alt="" className="ep-submitting-icon" />
            <h3 className="ep-gen-loading-title">Almost there!</h3>
            <p className="ep-gen-loading-text">We&#8217;re checking your answers and putting together your results. This may take a moment.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default JapaneseExamQuestions;
